import React, { useState } from "react";
import * as Mui from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { parse, format } from "date-fns";
import { designColors, designShell } from "services/constants/designTokens";

export const formatDisplayDate = (dateString) => {
  if (!dateString) return "";
  try {
    return format(parse(dateString, "dd-MM-yyyy", new Date()), "dd MMM yyyy");
  } catch (_error) {
    return dateString;
  }
};

// Exported so callers can size a sibling filter (e.g. Department's
// CompactHeaderSelectFilter via its buttonSx prop) to match this button's
// height — it's taller than the plain 40px compact filter row because of
// the second (date-range) line.
export const YEAR_FILTER_BUTTON_HEIGHT = 52;

const compactYearFilterButtonSx = {
  ...designShell.compactOutlineButton,
  width: "100%",
  minHeight: YEAR_FILTER_BUTTON_HEIGHT,
  height: YEAR_FILTER_BUTTON_HEIGHT,
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1,
  px: 2,
  py: 0.75,
  borderRadius: 1.5,
  backgroundColor: designColors.slate100,
  border: "none",
  boxShadow: "none",
  color: designColors.slate800,
  "&:hover": {
    backgroundColor: designColors.slate100,
    border: "none",
    boxShadow: "none",
  },
};

const optionKey = (option) => option?.id ?? option?.filteredValue;

// Same compact "pill button + Menu" shape used across the app's header
// filter rows (see the Roster screen, pages/TeamZone/PlanShift/index.js
// and its CompactHeaderSelectFilter) but with a two-line option (year name
// + its date range) since a bare year number doesn't tell you which dates
// it covers. Shared between LeaveSummary's page header and its side panel
// so both Year pickers look and behave the same way.
export const YearHeaderFilter = ({ options, selected, onSelect, label = "Year" }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <Mui.ButtonBase
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={compactYearFilterButtonSx}
      >
        <Mui.Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <CalendarMonthIcon sx={{ fontSize: 18, color: designColors.slate700, flexShrink: 0 }} />
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography
              sx={{
                minWidth: 0,
                fontSize: 13.5,
                fontWeight: 600,
                color: designColors.slate800,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "left",
              }}
            >
              {label} - {selected?.label || "Select"}
            </Mui.Typography>
            {selected ? (
              <Mui.Typography
                sx={{
                  fontSize: 11,
                  color: designColors.slate500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                }}
              >
                {formatDisplayDate(selected.start_date)} – {formatDisplayDate(selected.end_date)}
              </Mui.Typography>
            ) : null}
          </Mui.Box>
        </Mui.Stack>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: designColors.slate700, flexShrink: 0 }} />
      </Mui.ButtonBase>
      <Mui.Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            width: anchorEl?.offsetWidth || 240,
            borderRadius: "12px",
            border: `1px solid ${designColors.slate200}`,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          },
        }}
        MenuListProps={{ sx: { py: 0.75 } }}
      >
        {options.map((option, index) => {
          const isSelected = optionKey(option) === optionKey(selected);
          return (
            <React.Fragment key={optionKey(option)}>
              <Mui.MenuItem
                selected={isSelected}
                onClick={() => {
                  onSelect(option);
                  setAnchorEl(null);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  "&.Mui-selected": {
                    backgroundColor: designColors.tealTint,
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: designColors.tealTint,
                  },
                }}
              >
                <Mui.Stack spacing={0.4} sx={{ minWidth: 0 }}>
                  <Mui.Typography
                    sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate900 }}
                  >
                    {option.label}
                  </Mui.Typography>
                  <Mui.Stack direction="row" spacing={0.6} alignItems="center">
                    <CalendarMonthIcon sx={{ fontSize: 13, color: designColors.slate400 }} />
                    <Mui.Typography sx={{ fontSize: 12, color: designColors.slate500 }}>
                      {formatDisplayDate(option.start_date)} – {formatDisplayDate(option.end_date)}
                    </Mui.Typography>
                  </Mui.Stack>
                </Mui.Stack>
                {isSelected ? (
                  <CheckCircleIcon
                    sx={{ fontSize: 19, color: designColors.tealDark, flexShrink: 0 }}
                  />
                ) : null}
              </Mui.MenuItem>
              {index < options.length - 1 ? (
                <Mui.Divider sx={{ my: 0.25 }} />
              ) : null}
            </React.Fragment>
          );
        })}
      </Mui.Menu>
    </>
  );
};
